import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getUserIcon } from '../../../api/aws/s3';
import { usersRef } from '../../../api/firebase/firestore/user';
import {
  black,
  grayText,
  primaryButtonBackground
} from '../../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { role, screenType } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomGuideButton from '../../uiParts/CustomGuideButton';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import Sidebar from '../../uiParts/Sidebar';
import StaffListTable from '../../uiParts/StaffListTable';

import type { StaffType } from '../../uiParts/StaffListTable';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  card: {
    '@media screen and (max-width:480px)': {
      padding: '0'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px',
    padding: '0 40px'
  },
  contactButton: {
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px'
    },
    padding: '6px 16px'
  },
  contactButtonText: {
    color: black,
    fontSize: '14px'
  },
  content: {
    '@media screen and (max-width:480px)': {
      padding: '0'
    },
    textAlign: 'left'
  },
  grayText,
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
};

const ClientStaffList = (): React.ReactElement => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  useEffect(() => {
    document.title = t('title.client.staffList');
  });
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = React.useState<boolean>(false);
  const [staffAddDisabled, setStaffAddDisabled] = useState<boolean>(false);
  const [cantEdit, setCantEdit] = useState<boolean>(false);
  const [rows, setRows] = useState<StaffType[]>([]);

  const init = async (): Promise<void> => {
    setLoading(true);
    try {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        setStaffAddDisabled(userSnap.data()?.role === role.member);
        setCantEdit(userSnap.data()?.role === role.member);
        if (userSnap.exists()) {
          const companyId = userSnap.data()?.companyId as string;
          const users = query(
            usersRef,
            where('companyId', '==', companyId),
            where('registerStatus', '!=', 99),
            orderBy('registerStatus'),
            orderBy('created', 'desc')
          );
          const usersSnapshots = await getDocs(users);
          const iconPromises = usersSnapshots.docs.map(
            async (user: QueryDocumentSnapshot<DocumentData>) => {
              const icon = await getUserIcon(user);
              return {
                department: user.data()?.department as string,
                email: user.data()?.email as string,
                icon,
                id: user.data()?.uid as string,
                inviteFlg: user.data()?.inviteFlg as boolean,
                name: user.data()?.name as string,
                registerStatus: user.data()?.registerStatus as number,
                role: user.data()?.role as number
              };
            }
          );
          const staffRows = await Promise.all(iconPromises);
          setRows(staffRows);
        }
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
      setShowList(true);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }

    void (async () => {
      await init();
    })();
  }, []);

  return (
    <>
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.client} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.staffList')}
            </Typography>
            <Button
              variant="contained"
              sx={[
                primaryButtonBackground,
                {
                  '@media screen and (max-width:960px)': {
                    height: '30px'
                  }
                }
              ]}
              onClick={() => navigate('/client/mypage/staff/invite')}
              disabled={staffAddDisabled}
            >
              <AddIcon sx={{ color: '#FFF' }} />
              {t('mypage.staffAdd')}
              <CustomGuideButton
                title={t('mypage.staffAdd')}
                guideText={t('guideText.staff.addStaff')}
                color="white"
              />
            </Button>
          </Typography>
          {/* Table manager list start */}
          {showList && (
            <>
              <CustomLoading loading={loading} />
              <Grid container>
                <Grid item xs={12} sx={{ overflow: 'scroll' }}>
                  <Typography component="div">
                    <Card sx={[styles.card, { minWidth: '1056px' }]}>
                      <CardContent sx={styles.content}>
                        <StaffListTable
                          screen={screenType.client}
                          rows={rows}
                          cantEdit={cantEdit}
                        />
                      </CardContent>
                    </Card>
                  </Typography>
                </Grid>
              </Grid>
              <CustomSnackbar
                open={open}
                autoHideDuration={6000}
                onClose={() => setOpen(false)}
                text={errorText}
              />
            </>
          )}
          {/* Table staff list end */}
        </Typography>
      </Typography>
    </>
  );
};
export default ClientStaffList;
